import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {of} from 'rxjs';
import {EMPTY} from 'rxjs';
import {catchError, map, withLatestFrom, switchMap, filter} from 'rxjs/operators';
import * as RA from 'ramda-adjunct';
import {MenuApiService} from '../menu-api.service';
import {MenuStoreService} from '../menu-store.service';
import {ChangeSetsDialogComponent} from '@emm/shared/dialogs/change-sets-dialog/change-sets-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {FeatureScope} from '@emm/core/util/feature-scope.constants';
import {MenuBaseActions} from '@emm/menu/store/base/menu-base.actions';
import {ChangeSetsActions} from '@emm/menu/store/change-sets/change-sets.actions';
import {ConsumersActions} from '@emm/menu/store/consumer/consumers.actions';
import {FeatureScopeActions} from '@emm/menu/store/feature-scope/feature-scope.actions';
import {ItemDetailActions} from '@emm/menu/store/item-detail/item-detail.actions';
import {SubmenusActions} from '@emm/menu/store/submenus/submenus.actions';

const ITEM_WIDTH_750 = '750px';

@Injectable({
    providedIn: 'root'
})
export class ChangeSetsEffects {

    selectFeatureScope$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FeatureScopeActions.select),
            switchMap(() => of(ChangeSetsActions.loadAllFutureChangeSets()))
        )
    );

    loadAllFutureChangeSets$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeSetsActions.loadAllFutureChangeSets),
            switchMap(() => {
                    return this.api.getAllFutureChangeSets().pipe(
                        map((changeSets) => ChangeSetsActions.loadAllFutureChangeSetsSuccess({changeSets})),
                        catchError(({error}) => of(ChangeSetsActions.loadAllFutureChangeSetsFailure({error})))
                    );
                }
            )
        )
    );

    redirectToDate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeSetsActions.redirectToDate),
            switchMap(() => {
                    return this.api.getAllFutureChangeSets().pipe(
                        map((changeSets) => ChangeSetsActions.redirectToDateSuccess({changeSets})),
                        catchError(({error}) => of(ChangeSetsActions.redirectToDateFailure({error})))
                    );
                }
            )
        )
    );

    changeEffectiveDateTrigger$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeSetsActions.redirectToDate),
            switchMap(({date}) => of(MenuBaseActions.changeEffectiveDate({effectiveDate: date})))
        )
    );

    changeSetsTriggerConsumer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ConsumersActions.select),
            withLatestFrom(
                this.store.queryFeatureScope.selected$,
                this.store.queryConsumers.menuParamsContext$
            ),
            switchMap((value) => {
                if (value[1] !== 'Global') {
                    return of(ChangeSetsActions.loadFutureChangeSets());
                } else {
                    return EMPTY;
                }
            })
        )
    );

    changeSetsTriggerSubmenu$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SubmenusActions.select),
            switchMap(() => of(ChangeSetsActions.loadFutureChangeSets()))
        )
    );

    changeSetsTriggerPatch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ItemDetailActions.addSuccess,
                ItemDetailActions.deleteSuccess,
                ItemDetailActions.updateMenuItemSuccess,
                ItemDetailActions.pasteCascadeSuccess,
                ItemDetailActions.deleteCascadeSuccess,
                // ItemDetailActions.updateCascadeSuccess, // TODO include update cascade here
                ItemDetailActions.toggleDisabledSuccess,
                ItemDetailActions.toggleDisabledMenuWideSuccess,
                ItemDetailActions.copySuccess,
                ItemDetailActions.moveSuccess,
                ChangeSetsActions.redirectToDateSuccess
            ),
            switchMap(() => of(ChangeSetsActions.loadFutureChangeSets()))
        )
    );

    // load future change sets for full menus
    loadFutureChangeSetsMenu$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeSetsActions.loadFutureChangeSets),
            withLatestFrom(
                this.store.queryConsumers.selected$,
                this.store.queryLocationSets.selected$,
                this.store.queryFeatureScope.selected$,
            ),
            switchMap(([_, selectedConsumer, selectedLocationSet, featureScope]) => {
                const shouldLoad = RA.isNotNil(selectedConsumer) && RA.isNotNil(selectedLocationSet) &&
                    (featureScope === FeatureScope.Viewer);

                if (!shouldLoad) {
                    // Return empty success to reset loading state
                    return of(ChangeSetsActions.loadFutureChangeSetsSuccess({changeSets: []}));
                }

                const consumerTag = selectedConsumer?.tag;
                const locationSetTag = selectedLocationSet?.tag;

                return this.api.getFutureChangeSets(featureScope, consumerTag, locationSetTag, null).pipe(
                    map((changeSets) => ChangeSetsActions.loadFutureChangeSetsSuccess({changeSets})),
                    catchError(({error}) => of(ChangeSetsActions.loadFutureChangeSetsFailure({error})))
                );
            })
        )
    );

    // load future change set for submenus
    loadFutureChangeSetsSubmenu$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeSetsActions.loadFutureChangeSets),
            withLatestFrom(
                this.store.querySubmenus.selected$,
                this.store.querySubmenus.selectedSearchSubmenu$,
                this.store.queryLocationSets.selected$,
                this.store.queryFeatureScope.selected$,
            ),
            filter(([_, selectedSubmenu, searchSubmenu, selectedLocationSet, featureScope]) => {
                return RA.isNotNil(selectedLocationSet) &&
                    (featureScope === FeatureScope.Global || featureScope === FeatureScope.Client);
            }),
            map(([_, selectedSubmenu, searchSubmenu, selectedLocationSet, featureScope]) => ({
                submenuTag: searchSubmenu && searchSubmenu !== 'null' ? searchSubmenu : selectedSubmenu,
                locationSetTag: selectedLocationSet.tag,
            })),
            filter(({submenuTag, locationSetTag}) => {
                return RA.isNotNil(submenuTag);
            }),
            switchMap(({submenuTag, locationSetTag}) =>
                this.api.getFutureChangeSetsForSubmenu(submenuTag, locationSetTag, null).pipe(
                    map((changeSets) => ChangeSetsActions.loadFutureChangeSetsSuccess({changeSets})),
                    catchError(({error}) => of(ChangeSetsActions.loadFutureChangeSetsFailure({error})))
                )
            )
        )
    );


    openViewDialogMenu$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeSetsActions.openViewDialog),
            withLatestFrom(
                this.store.queryFeatureScope.selected$,
                this.store.queryBase.effectiveDate$,
                this.store.queryLocationSets.selected$,
                this.store.queryConsumers.selected$,
            ),
            filter(([_, featureScope]) => featureScope === FeatureScope.Viewer),
            filter(([_, featureScope, effectiveDate, location, consumer]) => RA.isNotNil(consumer) && RA.isNotNil(location)),
            switchMap(([_, featureScope, effectiveDate, location, consumer]) => {
                const dialogRef = this.dialog.open(ChangeSetsDialogComponent, {
                    data: {
                        effectiveDate,
                        location: location?.tag,
                        consumer: consumer?.tag,
                        submenu: null,
                        featureScope,
                    },
                    width: ITEM_WIDTH_750
                });

                return dialogRef.afterClosed().pipe(map(() => ChangeSetsActions.closeViewDialog())
                );
            })
        )
    );

    openViewDialogSubmenu$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ChangeSetsActions.openViewDialog),
            withLatestFrom(
                this.store.queryFeatureScope.selected$,
                this.store.queryBase.effectiveDate$,
                this.store.queryLocationSets.selected$,
                this.store.querySubmenus.selected$,
                this.store.querySubmenus.selectedSearchSubmenu$,
            ),
            filter(([_, featureScope]) => featureScope === FeatureScope.Global || featureScope === FeatureScope.Client),
            filter(([_, featureScope, effectiveDate, location, submenu, searchSubmenu]) =>
                (RA.isNotNil(submenu) || RA.isNotNil(searchSubmenu))),
            switchMap(([_, featureScope, effectiveDate, location, submenu, searchSubmenu]) => {
                const dialogRef = this.dialog.open(ChangeSetsDialogComponent, {
                    data: {
                        effectiveDate,
                        location: location?.tag,
                        consumer: null,
                        submenu: searchSubmenu && searchSubmenu !== 'null' ? searchSubmenu : submenu,
                        featureScope,
                    },
                    width: ITEM_WIDTH_750
                });

                return dialogRef.afterClosed().pipe(map(() => ChangeSetsActions.closeViewDialog())
                );
            })
        )
    );

    constructor(
        private actions$: Actions,
        private api: MenuApiService,
        private store: MenuStoreService,
        public dialog: MatDialog,
    ) {
    }

}
